/**
 * Form type agnostic File upload
 */
import { CloudArrowUpIcon, TrashIcon } from '@heroicons/react/24/outline'
import { isEmpty, pick, set } from 'lodash'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone'
import { useIntl } from 'react-intl'
import { z } from 'zod'

import GenericInput, { FormComponent } from '@/components/GenericForm/GenericInput'
import Button from '@/components/TailwindUI/Button'
import ConfirmDialog, { Actions } from '@/components/TailwindUI/ConfirmDialog'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import {
  fetchUploadRequestV2,
  requestDeleteAsset,
  uploadFileWithSignedUploadRequest
} from '@/utilities/assetUploadServices'
import cn from '@/utilities/cn'

import messages from './messages'

type Asset = 'attachment' | 'image'
interface FileUploadProps extends FormComponent<Attachment> {
  accept?: Array<string>
  assetType?: Asset
  className?: string
  onChange: (value?: Attachment) => void
  onDrop?: <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[], event: DropEvent) => void
  setIsProcessing?: (isProcessing: boolean) => void
  removeAttachment?: (onRemoveAttachment: () => void) => void
}
export const attachmentSchema = z.object({
  name: z.string({ required_error: 'File is required' }),
  textSigned: z.string().optional(),
  text: z.string(),
  size: z.number(),
  assetId: z.string(),
  url: z.string().url()
})
export type Attachment = z.infer<typeof attachmentSchema>

type ActionState = {
  action: Actions
  isOpen: boolean
}

const getHumanReadableSize = (sizeInBytes: any) => {
  return sizeInBytes > 1_000_000
    ? `${Math.round(sizeInBytes / 100_000) / 10} MB`
    : `${Math.round(sizeInBytes / 1000)} kB`
}

const getAcEntry = (attachment: any) => {
  const showSize = getHumanReadableSize(attachment.size)
  return {
    ...attachment,
    text: `[${attachment.name}](${attachment.url}) (${showSize})`
  }
}
/**
 * Text formatter for displaying accepted types
 */
const formatter = new Intl.ListFormat(undefined, {
  style: 'long',
  type: 'disjunction'
})

const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      formatProps,
      value,
      accept,
      assetType = 'attachment',
      className,
      disabled = false,
      onChange,
      onDrop,
      setIsProcessing = () => {},
      removeAttachment = () => {}
    },
    ref
  ) => {
    const { error } = formatProps
    const intl = useIntl()
    const [action, setAction] = useState<Actions>(null)
    const [uploadError, setUploadError] = useState<any>({})
    const currentAssetId = useRef()
    const [confirmAction, setConfirmAction] = useState<ActionState>({
      action: null,
      isOpen: false
    })

    const defaultDrop = async (acceptedFiles: File[]) => {
      setUploadError({})
      setIsProcessing(true)
      if (acceptedFiles?.[0]) {
        setAction('processing')
        const processingFile = acceptedFiles?.[0] || {}
        try {
          const { postData, id, url } = await fetchUploadRequestV2(processingFile, assetType)
          set(processingFile, 'assetId', id)
          set(processingFile, 'url', url)
          currentAssetId.current = id

          const { promise: fileUploadPromise } = uploadFileWithSignedUploadRequest(postData, processingFile)
          fileUploadPromise.then(() => {
            const acEntry = getAcEntry(processingFile)
            set(acEntry, 'size', processingFile.size)
            set(acEntry, 'name', processingFile.name)

            const file = pick(acEntry, ['name', 'size', 'assetId', 'url', 'text'])
            onChange(file)
            setIsProcessing(false)
          })
        } catch {
          setAction(null)
          setUploadError({ upload: intl.formatMessage(messages.uploadFailure) })
          onChange()
          currentAssetId.current = undefined
          setIsProcessing(false)
        }
      } else {
        setAction(null)
        accept &&
          setUploadError({
            type: intl.formatMessage(messages.invalidUploadFileType, {
              types: formatter.format(accept)
            })
          })
        onChange()
        currentAssetId.current = undefined
        setIsProcessing(false)
      }
    }

    const requestDelete = () => {
      setConfirmAction({
        action: 'deleting',
        isOpen: true
      })
    }

    const handleDelete = async () => {
      setAction('deleting')
      if (value?.assetId) {
        requestDeleteAsset(value?.assetId)
      }
      onChange()
      setAction(null)
      setConfirmAction({ ...confirmAction, isOpen: false })
    }

    const reset = useCallback(() => {
      const assetId = currentAssetId.current
      if (assetId) {
        requestDeleteAsset(assetId)
      }
      setAction(null)
      setUploadError({})
      onChange()
      setIsProcessing(false)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      removeAttachment?.(reset)
    }, [removeAttachment, reset])

    const { getRootProps, getInputProps } = useDropzone({
      onDrop: onDrop || defaultDrop,
      accept,
      maxFiles: 1,
      disabled
    })

    return (
      <>
        <ConfirmDialog
          isOpen={confirmAction?.isOpen}
          onConfirm={handleDelete}
          onCancel={() => setConfirmAction({ ...confirmAction, isOpen: false })}
          message={intl.formatMessage(messages.deleteMessage)}
          variant="error"
          cancelText={intl.formatMessage(messages.cancelDelete)}
          confirmText={intl.formatMessage(messages.confirmDelete)}
          loadingText={intl.formatMessage(messages.deleteLoading)}
          loading={action === confirmAction.action}
          subMessage={
            <>
              <span className="font-bold">{value?.name}</span>
              <span> {intl.formatMessage(messages.deleteSubMessage)}</span>
            </>
          }
        />
        <GenericInput {...formatProps} disabled={disabled}>
          <div
            {...getRootProps()}
            ref={ref}
            className={cn(
              error
                ? 'cursor-pointer border-red-300 text-red-500 focus:border-red-600'
                : disabled
                  ? 'border-zinc-300 bg-zinc-100 text-zinc-300'
                  : 'cursor-pointer border-brand-100 bg-brand-50 text-brand-300 hover:border-brand-200',

              'flex h-12 w-full flex-row items-center justify-center rounded-lg border-2 border-dashed',
              className
            )}
          >
            {isEmpty(value) ? (
              action === 'processing' ? (
                <>
                  <LoadingSpinner className="mr-2 h-6 w-6" />
                  <p className="text-left text-sm font-semibold">{intl.formatMessage(messages.uploading)}</p>
                </>
              ) : uploadError?.upload ? (
                <>
                  <p className="mt-1 text-sm text-red-500">{intl.formatMessage(messages.uploadFailure)}</p>
                  <Button color="primary" onClick={reset}>
                    {intl.formatMessage(messages.tryAgain)}
                  </Button>
                </>
              ) : (
                <>
                  <input {...getInputProps()} />
                  <CloudArrowUpIcon className="h-6 w-6" />
                  <p className="ml-2 text-sm">{intl.formatMessage(messages.dropZoneDescription)}</p>
                </>
              )
            ) : (
              <div className="flex flex-row items-center justify-between pt-5 pb-6 text-zinc-500">
                {!disabled && (
                  <TrashIcon
                    className="mr-3 h-5 w-5 cursor-pointer text-right text-zinc-400 hover:text-red-600"
                    onClick={() => {
                      !disabled && requestDelete()
                    }}
                  />
                )}

                <p className="mt-1 text-left text-sm font-semibold">{value.name}</p>
                <p className="ml-1 mt-1 text-sm">{`(${getHumanReadableSize(value.size)})`}</p>
              </div>
            )}
          </div>
        </GenericInput>
        {uploadError?.type && <p className="mt-1 text-sm text-red-500">{uploadError.type}</p>}
      </>
    )
  }
)

FileUpload.displayName = 'FileUpload'
export default FileUpload
